import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ListHeader from './list_header';
import EventHeader from './event_header';
import ChevronIcon from './chevron_icon';
import { splitEventsByFavorites, noTournamentsMessage } from '../utils/events_utils';
import { themedTextClass } from '../utils/css_utils';
import { summaryTitle } from '../actions/summaries_index';
import { componentText } from '../utils/i18n_utils';

const isSelectedSpec = (selectedEventId, events) =>
  events.some((event) => event.id === selectedEventId || event.snapshots.some((s) => s.id === selectedEventId));

const isSelectedEvent = (selectedEventId, id, snapshots) =>
  selectedEventId === id || snapshots.some((s) => s.id === selectedEventId);

const selectedSpec = (selectedEventId, eventsList) =>
  Object.entries(eventsList).find(([, events]) =>
    isSelectedSpec(selectedEventId, events)
  )?.[0] ?? null;

const EventsListModal = ({
  eventsList,
  favoritesByEvent,
  selectedRoundId,
  selectedEventId,
  selectedSummaryKey,
  enabledSummaryKeys = [],
  showEmptyState = false,
  handleClickEvent,
  handleClickSummary,
  handleClickEmptyRound,
  isManagerSite,
}) => {
  // Managers see everything as "other" - no favorites split, and so neither
  // list header. Same rule as EventsList, which owns the list behind this modal.
  const { favoriteEvents, otherEvents } = isManagerSite
    ? { favoriteEvents: {}, otherEvents: eventsList }
    : splitEventsByFavorites(eventsList, favoritesByEvent);

  // A selected summary owns the highlight; ignore any leftover event selection.
  const highlightedEventId = selectedSummaryKey ? null : selectedEventId;

  const initialOpenSpecs = [
    selectedSpec(highlightedEventId, favoriteEvents),
    selectedSpec(highlightedEventId, otherEvents),
  ].filter(Boolean);

  const [openSpecs, setOpenSpecs] = useState(() => new Set(initialOpenSpecs));

  const toggleSpec = (specId) =>
    setOpenSpecs((prev) => {
      const next = new Set(prev);
      next.has(specId) ? next.delete(specId) : next.add(specId);
      return next;
    });

  const hasFavorites = Object.keys(favoriteEvents).length > 0;
  const hasOthers = Object.keys(otherEvents).length > 0;

  const renderSpecGroup = (specId, events) => {
    const isGroup = events.length > 1;
    return (
      <React.Fragment key={specId}>
        {isGroup && (
          <EventHeader id={specId}>
            <button
              className={`lbv2__spec-btn ${isSelectedSpec(highlightedEventId, events) ? themedTextClass(isManagerSite) : ''}`}
              onClick={() => toggleSpec(specId)}
              aria-expanded={openSpecs.has(specId)}
            >
              <span className='lbv2__spec'>{events[0].tournamentSpecName}</span>
              <ChevronIcon isOpen={openSpecs.has(specId)} />
            </button>
          </EventHeader>
        )}

        {(openSpecs.has(specId) || !isGroup) &&
          events.map(({ id, name, snapshots }) => (
            <EventHeader key={id} id={id}>
              <button
                className={`lbv2__event-btn ${isGroup ? 'in-group' : ''} ${isSelectedEvent(highlightedEventId, id, snapshots) ? `is-selected ${themedTextClass(isManagerSite)}` : ''}`}
                onClick={() => handleClickEvent(id, selectedRoundId, eventsList)}
              >
                {name}
              </button>
            </EventHeader>
          ))
        }
      </React.Fragment>
    );
  };

  return (
    <div className="lbv2__events-list">
      {enabledSummaryKeys.length > 0 && handleClickSummary && (
        <ol className="lbv2__without-favorites lbv2__summaries-modal-list">
          {enabledSummaryKeys.map((summaryKey) => (
            <EventHeader key={summaryKey} id={summaryKey}>
              <button
                className={`lbv2__event-btn ${selectedSummaryKey === summaryKey ? `is-selected ${themedTextClass(isManagerSite)}` : ''}`}
                onClick={() => handleClickSummary(summaryKey, selectedRoundId, eventsList)}
              >
                {summaryTitle(summaryKey)}
              </button>
            </EventHeader>
          ))}
        </ol>
      )}
      {/* Summary entries stay listed and clickable above the message. In the
          rounds modal (a previewed round provides selectedRoundId and the
          handler) the message is an event-row-styled button that commits the
          empty round, so the user can land on that round's board; the plain
          events modal keeps the static boxed message. */}
      {showEmptyState && (
        handleClickEmptyRound && selectedRoundId != null
          ? (
            <ol className="lbv2__without-favorites">
              <EventHeader id={`lbv2__empty-round-${selectedRoundId}`}>
                <button
                  type="button"
                  className="lbv2__event-btn"
                  onClick={() => handleClickEmptyRound(selectedRoundId, eventsList)}
                >
                  {noTournamentsMessage()}
                </button>
              </EventHeader>
            </ol>
          )
          : <div className="lbv2__empty-message">{noTournamentsMessage()}</div>
      )}
      {hasFavorites && (
        <>
          {hasOthers && <ListHeader>{componentText('events_list.my_leaderboards', 'My Leaderboards')}</ListHeader>}
          <ol className="lbv2__with-favorites">
            {Object.entries(favoriteEvents).map(([specId, events]) =>
              renderSpecGroup(specId, events)
            )}
          </ol>
          {hasOthers && <ListHeader>{componentText('events_list.leaderboards', 'Leaderboards')}</ListHeader>}
        </>
      )}

      {hasOthers && (
        <ol className="lbv2__without-favorites">
          {Object.entries(otherEvents).map(([specId, events]) =>
            renderSpecGroup(specId, events)
          )}
        </ol>
      )}
    </div>
  );
};

EventsListModal.propTypes = {
  eventsList: PropTypes.object,
  favoritesByEvent: PropTypes.object,
  selectedRoundId: PropTypes.string,
  selectedEventId: PropTypes.string,
  selectedSummaryKey: PropTypes.string,
  enabledSummaryKeys: PropTypes.array,
  showEmptyState: PropTypes.bool,
  handleClickEvent: PropTypes.func.isRequired,
  handleClickSummary: PropTypes.func,
  handleClickEmptyRound: PropTypes.func,
  isManagerSite: PropTypes.bool,
};

export default EventsListModal;
